import React from 'react'
import { motion } from 'framer-motion'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiSun, FiMoon, FiMonitor, FiUser, FiLogOut, FiCheck, FiShield, FiBell } = FiIcons

const Settings = () => {
  const { theme, setTheme } = useTheme()
  const { user, profile, signOut } = useAuth()

  const themeOptions = [
    { 
      id: 'light', 
      label: 'Light', 
      icon: FiSun,
      desc: 'Professional clarity'
    },
    { 
      id: 'dark', 
      label: 'Dark', 
      icon: FiMoon,
      desc: 'Focus & depth'
    },
    { 
      id: 'system', 
      label: 'Auto', 
      icon: FiMonitor,
      desc: 'Device sync'
    }
  ]

  return (
    <div className="max-w-5xl mx-auto pb-20">
      <header className="mb-12">
        <h1 className="text-4xl font-black tracking-tight text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400">Settings</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-2 font-medium">Fine-tune your professional trading environment</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Appearance */}
        <div className="lg:col-span-2 space-y-8">
          <section className="bg-white dark:bg-lux-dark-card rounded-3xl border border-lux-light-border dark:border-lux-dark-border p-8 shadow-sm">
            <div className="flex items-center space-x-3 mb-8">
              <div className="p-2 bg-lux-primary/10 rounded-lg">
                <SafeIcon icon={FiMonitor} className="h-5 w-5 text-lux-primary" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Interface Theme</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {themeOptions.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setTheme(opt.id)}
                  className={`group relative flex flex-col p-6 rounded-2xl border-2 transition-all duration-500 ${
                    theme === opt.id 
                      ? 'border-lux-primary bg-lux-primary/[0.02] ring-4 ring-lux-primary/5' 
                      : 'border-lux-light-border dark:border-lux-dark-border hover:border-gray-300 dark:hover:border-slate-600'
                  }`}
                >
                  <div className={`p-3 rounded-xl mb-4 self-start transition-colors duration-500 ${
                    theme === opt.id ? 'bg-lux-primary text-white shadow-lg shadow-lux-primary/20' : 'bg-gray-100 dark:bg-slate-800 text-gray-500'
                  }`}>
                    <SafeIcon icon={opt.icon} className="h-6 w-6" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-bold text-gray-900 dark:text-white">{opt.label}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{opt.desc}</p>
                  </div>
                  {theme === opt.id && (
                    <div className="absolute top-4 right-4 bg-lux-primary text-white p-1 rounded-full">
                      <SafeIcon icon={FiCheck} className="h-3 w-3" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </section>

          <section className="bg-white dark:bg-lux-dark-card rounded-3xl border border-lux-light-border dark:border-lux-dark-border p-8 shadow-sm">
            <div className="flex items-center space-x-3 mb-8">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <SafeIcon icon={FiBell} className="h-5 w-5 text-orange-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Notifications</h2>
            </div>
            <div className="space-y-4">
              {['Trade Confirmations', 'AI Insight Alerts', 'Weekly Performance Recap'].map((item) => (
                <div key={item} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-900/50 rounded-2xl border border-lux-light-border dark:border-lux-dark-border">
                  <span className="font-semibold text-gray-700 dark:text-gray-300">{item}</span>
                  <div className="h-6 w-11 bg-gray-200 dark:bg-slate-700 rounded-full relative cursor-pointer">
                    <div className="absolute top-1 left-1 h-4 w-4 bg-white rounded-full transition-all" />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Right Column: Account */}
        <div className="space-y-8">
          <section className="bg-white dark:bg-lux-dark-card rounded-3xl border border-lux-light-border dark:border-lux-dark-border overflow-hidden shadow-sm">
            <div className="bg-gradient-to-br from-lux-primary to-lux-accent p-8 text-white relative">
              <div className="relative z-10">
                <div className="h-16 w-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center text-2xl font-bold mb-4 border border-white/30">
                  {profile?.display_name?.[0] || user?.email?.[0].toUpperCase()}
                </div>
                <h3 className="text-xl font-bold">{profile?.display_name || 'Account'}</h3>
                <p className="text-sm text-white/70 truncate">{user?.email}</p>
              </div>
              <div className="absolute top-0 right-0 p-8 opacity-20">
                <SafeIcon icon={FiShield} className="h-24 w-24" />
              </div>
            </div>
            <div className="p-8 space-y-4">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500 font-medium">Status</span>
                <span className="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-lg font-bold text-[10px] uppercase tracking-wider">Pro Plan</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500 font-medium">Experience</span>
                <span className="text-gray-900 dark:text-white font-bold capitalize">{profile?.trading_experience || 'Beginner'}</span>
              </div>
              <hr className="border-lux-light-border dark:border-lux-dark-border" />
              <button 
                onClick={() => signOut()}
                className="w-full flex items-center justify-center space-x-2 py-3 rounded-2xl bg-red-50 dark:bg-red-900/10 text-red-600 font-bold hover:bg-red-100 dark:hover:bg-red-900/20 transition-all border border-red-100 dark:border-red-900/20"
              >
                <SafeIcon icon={FiLogOut} className="h-4 w-4" />
                <span>Sign Out Account</span>
              </button>
            </div>
          </section>

          <div className="p-6 bg-lux-primary/5 dark:bg-lux-primary/10 rounded-3xl border border-lux-primary/10">
            <h4 className="font-bold text-lux-primary text-sm mb-2">Need Help?</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              Access 24/7 priority support for Pro members. Join our community of elite traders.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Settings